import {
  CheckCircle2,
  CircleSlash,
  FileText,
  History,
  PackageCheck,
  Send,
  Settings,
  Trash2,
  Truck,
  Undo2,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { LifecycleTimeline } from '../../../../components/LifecycleTimeline';
import { Menu } from '../../../../components/Menu';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { approvalsApi } from '../../../approvals/approvalsApi';
import type { ApprovalRequestDetail } from '../../../approvals/approvalTypes';
import { CancelPurchaseOrderModal } from '../components/CancelPurchaseOrderModal';
import { PurchaseOrderLinesPanel } from '../components/PurchaseOrderLinesPanel';
import { PurchaseOrderModal } from '../components/PurchaseOrderModal';
import { purchaseOrdersApi } from '../purchaseOrdersApi';
import { PO_STATUS } from '../purchaseOrderTypes';
import type {
  PurchaseOrderDetail,
  PurchaseOrderItemRollup,
} from '../purchaseOrderTypes';
import { purchaseOrderLifecycle } from '../utils/purchaseOrderLifecycle';

type TabKey = 'info' | 'lines' | 'approval';

/**
 * One purchase order — what was committed to, from whom, against which requests.
 *
 * On the **header** layout: an order reads as one continuous thing rather than a record whose
 * content splits into panels a reader picks between.
 */
export function PurchaseOrderProfilePage() {
  const { id } = useParams<{ id: string }>();
  const canReadApprovals = usePermission('approvals.read');
  const canReadRequests = usePermission('purchase-requests.read');

  const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  /*
    A BANNER ACTION ALWAYS CONFIRMS. Marking an order sent is a record-level lifecycle move with
    no way back — and it is what opens the order to a goods receipt, so a stray click on the
    first control the reader's pointer crosses would let a delivery be booked against a document
    nobody posted.
  */
  const [markingSent, setMarkingSent] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('lines');

  /*
    THE APPROVAL IS FETCHED, not carried on the order — `PurchaseOrderDetail` holds an
    `approvalRequestId` and the engine's own endpoint holds the chain. Widening this module's DTO
    to carry another module's steps and instants would be a copy of that record, which is the
    thing the id exists to avoid.
  */
  const [fetchedApproval, setFetchedApproval] = useState<{
    forId: string;
    detail: ApprovalRequestDetail;
  } | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      purchaseOrdersApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setOrder(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this order.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    `!= null`, never `!==`: the API omits a null property entirely, so an unsubmitted order's
    `approvalRequestId` arrives as `undefined` and a strict comparison would fire a request for
    `/api/approvals/undefined` on every draft.

    IT FAILS SILENT, and deliberately. A reader without `approvals.read` gets a 403 here, and
    blanking the record they came to read over a panel they cannot see would be the failed-refresh
    mistake the profile rules name. The tab is permission-gated anyway.
  */
  const approvalId = order?.approvalRequestId;

  useEffect(() => {
    if (approvalId == null || !canReadApprovals) return;

    const controller = new AbortController();

    approvalsApi
      .get(approvalId, controller.signal)
      .then((detail) => {
        if (!controller.signal.aborted) setFetchedApproval({ forId: approvalId, detail });
      })
      .catch(() => {});

    return () => controller.abort();
  }, [approvalId, canReadApprovals]);

  async function submit() {
    if (!order) return;

    setActing(true);
    setError(null);

    try {
      await purchaseOrdersApi.submit(order.id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not submit this order.', 0),
      );
    } finally {
      setActing(false);
    }
  }

  async function markSent() {
    if (!order) return;

    setActing(true);
    setError(null);

    try {
      await purchaseOrdersApi.markSent(order.id);
      setMarkingSent(false);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not record this order as sent.', 0),
      );
    } finally {
      setActing(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Purchasing',
    { label: 'Purchase Orders', to: '/procurement/purchasing/purchase-orders' },
    order?.orderNumber ?? 'Order',
  ];

  if (loading && !order) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-5 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (!order) return null;

  const live = order.items.filter((l) => l.isActive);
  const banner = bannerFor(order);

  /*
    DERIVED during render rather than held in state: it is a pure function of the record and the
    approval, so storing it would leave a stale timeline on screen for a paint after every refresh.
  */
  const approval =
    fetchedApproval?.forId === order.approvalRequestId ? fetchedApproval.detail : null;
  const lifecycle = purchaseOrderLifecycle(order, approval);

  /*
    THE MERGED ROWS — the reader's own call of 29 Sep 2026: two lines, one per source request,
    with a merged total. It is drawn only where an item appears on MORE THAN ONE line; on one the
    merged total is that line's, and printing it would be the same value in two slots.
  */
  const merged = order.itemRollup.filter((r) => r.lineCount > 1);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        /*
          THE PANEL MODE IS PER TAB. Info is a stack of cards and takes the capped `cards`
          measure; the Lines tab is a table that owns its own chrome and its own scroll, which is
          what `bleed` is for.
        */
        panel={tab === 'lines' ? 'bleed' : 'cards'}
        id="purchase-order-profile"
        header={
          <ProfileHeader
            id="purchase-order-profile-header"
            icon={<Truck className="h-6 w-6" aria-hidden="true" />}
            title={order.orderNumber}
            meta={[
              order.supplierName,
              `${live.length} line${live.length === 1 ? '' : 's'}`,
              formatMoney(order.totalAmount),
              order.expectedDeliveryDate
                ? `Expected ${formatDateOnly(order.expectedDeliveryDate)}`
                : null,
            ].filter((v): v is string => Boolean(v))}
            /*
              THE SOURCE REQUESTS ON THE SECOND LINE, which is what this module has and its source
              does not: an order composed of two requests is a fact a reader needs at the top
              rather than a tab away.
            */
            secondary={[
              order.sourceRequests.length === 0
                ? null
                : `From ${order.sourceRequests.map((r) => r.requestNumber).join(', ')}`,
            ].filter((v): v is string => Boolean(v))}
            status={
              order.isActive ? (
                <StatusText tone="active">Active</StatusText>
              ) : (
                <StatusText tone="inactive">Deleted</StatusText>
              )
            }
            createdBy={order.createdByName}
            createdAt={order.createdAt}
            actions={
              <Menu
                id="purchase-order-profile-cog"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    permission: 'audit-logs.read',
                    onClick: () => setHistoryOpen(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Order options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          <NextActionAlert
            icon={<banner.icon className="h-4 w-4" aria-hidden="true" />}
            title={banner.title}
            description={banner.description}
            action={
              <div className="flex items-center gap-2">
                {order.status === PO_STATUS.draft && order.isActive ? (
                  <PermissionGate permission="purchase-orders.submit">
                    <Button
                      id="purchase-order-submit"
                      variant="primary"
                      onClick={() => void submit()}
                      disabled={acting || live.length === 0}
                      /*
                        THE GLYPH GOES THROUGH `leading`, never as a child. A Button renders its
                        children inside one <span>, and preflight sets `svg { display: block }` —
                        a block box in inline content, which pushes the label onto a second line.
                      */
                      leading={<Send className="h-4 w-4" aria-hidden="true" />}
                    >
                      Submit for approval
                    </Button>
                  </PermissionGate>
                ) : null}

                {/*
                  THE DIRECTIVE AFTER APPROVAL — product call, 01 Oct 2026. `canMarkSent` is the
                  SERVER's answer, from the one predicate the endpoint refuses on, so this button
                  and that refusal cannot disagree about whether the order is waiting to go out.
                */}
                {order.canMarkSent ? (
                  <PermissionGate permission="purchase-orders.send">
                    <Button
                      id="purchase-order-mark-sent"
                      variant="primary"
                      onClick={() => setMarkingSent(true)}
                      disabled={acting}
                      leading={<Truck className="h-4 w-4" aria-hidden="true" />}
                    >
                      Mark as sent
                    </Button>
                  </PermissionGate>
                ) : null}

                {order.status !== PO_STATUS.cancelled && order.isActive ? (
                  <PermissionGate permission="purchase-orders.submit">
                    <Button
                      id="purchase-order-cancel"
                      variant="secondary"
                      onClick={() => setCancelling(true)}
                      disabled={acting}
                      leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                    >
                      Cancel order
                    </Button>
                  </PermissionGate>
                ) : null}
              </div>
            }
            id="purchase-order-profile-banner"
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'lines',
            label: (
              <>
                Lines <CountBadge count={live.length} />
              </>
            ),
          },
          /*
            GATED ON `approvals.read`, which is the key of what it READS rather than the key of
            the page it sits on. `<FolderTabs>` hides a gated tab and lands the reader on the
            first one they can see, so a reader holding only `purchase-orders.read` meets Info
            with no dead strip.
          */
          {
            key: 'approval',
            permission: 'approvals.read',
            label: (
              <>
                Life cycle <CountBadge count={lifecycle.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {error ? (
          <div role="alert" className="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {tab === 'approval' ? (
          <ProfileCard id="purchase-order-profile-cycle" title="Life cycle" padded>
            {/*
              NO HEADING OF ITS OWN. The card above it is already titled `Life cycle`, so a
              second heading inside it is the same word in two slots — §8, one band apart. It
              read `Lifecycle` under a card reading `Approval cycle`, which was two words for
              one concept on one screen; the rename closed the first half and this closes the
              second. The purchase-request profile already took this shape.
            */}
            <LifecycleTimeline
              id="purchase-order-lifecycle"
              events={lifecycle}
              title={null}
              empty="Nothing has happened to this order yet."
            />
          </ProfileCard>
        ) : tab === 'info' ? (
          <>
            <ProfileCard
              id="purchase-order-profile-order"
              title="Order"
              onEdit={() => setEditing(true)}
              editPermission="purchase-orders.update"
              /*
                DISABLED FOR STATE with the reason, rather than hidden. `editPermission` HIDES,
                which would tell the reader they lack a grant rather than that the record has
                moved on.
              */
              editDisabledReason={
                order.isEditable
                  ? undefined
                  : `This order is ${order.statusLabel.toLowerCase()}, so it can no longer be changed. Only a draft is still the buyer's to edit.`
              }
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'PO number', value: order.orderNumber, mono: true },
                  { label: 'Supplier', value: order.supplierName },
                  { label: 'Supplier code', value: order.supplierCode, mono: true },
                  { label: 'Stage', value: order.statusLabel },
                  {
                    label: 'Expected delivery',
                    value: order.expectedDeliveryDate
                      ? formatDateOnly(order.expectedDeliveryDate)
                      : null,
                    mono: true,
                  },
                  { label: 'Payment terms', value: order.paymentTerms },

                  /*
                    WHO POSTED THE DOCUMENT AND WHEN — product call, 02 Oct 2026, in the
                    reader's own words: *when tagging PO as sent, add sent by/date*.

                    BOTH ROWS ARE DRAWN ON EVERY ORDER, including one not sent yet, where they
                    read an em dash. A row that appeared and vanished with the status would make
                    the card a different shape per record, and a reader could not tell a fact
                    that is missing from one that does not apply — `<PreviewTable>`'s own rule.

                    AN INSTANT, so `formatDateTime` rather than `formatDateOnly`: the stamp is a
                    `DateTime` the server took off the clock, and putting it through the
                    calendar-date path would show a reader west of UTC the day before the one on
                    the record.

                    THE NAME IS NOT `mono`. It is a person's label rather than a machine value —
                    the same call `Created by` in the header takes.
                  */
                  {
                    label: 'Sent to supplier',
                    value: order.sentAt == null ? null : formatDateTime(order.sentAt),
                    mono: true,
                  },
                  { label: 'Sent by', value: order.sentByName },

                  { label: 'Lines', value: String(live.length), mono: true },
                  { label: 'Total quantity', value: String(order.totalQuantity), mono: true },
                  { label: 'Total', value: formatMoney(order.totalAmount), mono: true },
                  { label: 'Notes', value: order.notes },
                  { label: 'Cancelled because', value: order.cancellationReason },
                ]}
              />
            </ProfileCard>

            {/*
              THE SOURCE REQUESTS — the card this module has and the source does not, because
              there an order has exactly one and it is a field.

              IT LINKS OUT rather than editing in place: a purchase request is ANOTHER module's
              record, which is `<ProfileCard>`'s third exemption. The link carries
              `purchase-requests.read` and is HIDDEN without it, never an implied read — that
              would hand the whole request module to every reader of this register.

              NO COG AT ALL, which is the narrower half of that exemption: which requests an
              order draws on is not a field anybody sets, it is whatever its lines fulfil. So the
              link-out is a ROW rather than a cog entry.
            */}
            <ProfileCard
              id="purchase-order-profile-sources"
              title={
                order.sourceRequests.length === 1 ? 'Source request' : 'Source requests'
              }
            >
              <PreviewTable
                variant="table-display"
                rows={
                  order.sourceRequests.length === 0
                    ? [{ label: 'Requests', value: null }]
                    : order.sourceRequests.map((source) => ({
                        label: source.requestNumber,
                        value: canReadRequests ? (
                          <Link
                            to={`/procurement/purchasing/purchase-requests/${source.purchaseRequestId}`}
                            className="font-medium text-slate-800 hover:underline"
                          >
                            {source.lineCount} line{source.lineCount === 1 ? '' : 's'} ·{' '}
                            {formatMoney(source.lineTotal)}
                          </Link>
                        ) : (
                          `${source.lineCount} line${source.lineCount === 1 ? '' : 's'} · ${formatMoney(source.lineTotal)}`
                        ),
                      }))
                }
              />
            </ProfileCard>

            {/*
              THE MERGED TOTALS — drawn only where an item genuinely appears on more than one
              line. On an ordinary order this card is absent, which is right: a card of one row
              that repeats the line beside it says nothing.

              IT CARRIES NO COG, and that is a FOURTH case of the no-Edit rule rather than a
              missed one: it came from no form and states a DERIVATION of this record's own
              lines, which is the Audit card's own family — that card is exempt because it
              "came from no form, records what happened". An Edit here would be a second way
              into the order's line editor, and "one card gets ONE Edit" refuses that; the
              lines are corrected from the Order card above.
            */}
            {merged.length > 0 ? (
              <ProfileCard
                id="purchase-order-profile-rollup"
                title="Merged by item"
                padded
              >
                <p className="mb-3 text-xs text-slate-500">
                  These items were ordered against more than one purchase request. The lines stay
                  separate so each keeps its trace back to the request that asked for it; this is
                  what the order is for in total.
                </p>

                <PreviewGrid<PurchaseOrderItemRollup>
                  columns={[
                    { header: 'Item', width: 240, render: (r) => <TextCell value={r.itemIdentity} /> },
                    { header: 'SKU', width: 140, render: (r) => <TextCell value={r.sku} mono /> },
                    { header: 'Unit', width: 80, render: (r) => <TextCell value={r.unit} /> },
                    {
                      header: 'Lines',
                      width: 80,
                      align: 'right',
                      render: (r) => <TextCell value={String(r.lineCount)} mono numeric />,
                    },
                    {
                      header: 'Qty',
                      width: 90,
                      align: 'right',
                      render: (r) => <TextCell value={String(r.orderedQuantity)} mono numeric />,
                    },
                    {
                      header: 'Total',
                      width: 140,
                      align: 'right',
                      render: (r) => <TextCell value={formatMoney(r.lineTotal)} mono numeric />,
                    },
                  ]}
                  rows={merged}
                  rowKey={(r) => r.inventoryItemId}
                  empty="Nothing is ordered twice on this order."
                />
              </ProfileCard>
            ) : null}

            {order.approvalRequestId ? (
              <ProfileCard id="purchase-order-profile-approval" title="Approval">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'Decision', value: order.approvalStatusLabel },
                    {
                      label: 'Chain',
                      value: canReadApprovals ? (
                        <Link
                          to={`/approvals/requests/${order.approvalRequestId}`}
                          className="font-medium text-slate-800 hover:underline"
                        >
                          Open the approval
                        </Link>
                      ) : (
                        // The reader can see that an approval EXISTS and is not being given a way
                        // into another module they have no grant on — hide, never disable.
                        'Hidden'
                      ),
                    },
                  ]}
                />
              </ProfileCard>
            ) : null}

            <ProfileCard id="purchase-order-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: order.createdByName },
                  { label: 'Created', value: formatDateTime(order.createdAt), mono: true },
                  { label: 'Updated by', value: order.updatedByName },
                  {
                    label: 'Updated',
                    value: order.updatedAt ? formatDateTime(order.updatedAt) : null,
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          /*
            FULL WIDTH inside the shell's `bleed` panel, and NO `<ProfileCard>` around it: a card
            draws a border and a title, and the tab already carries the word `Lines` and its count.

            THE INSET BELONGS TO THE WRAPPER: a `bleed` panel gives its child no gap, so without
            `p-4` the search group’s border sits on the panel’s border and the table runs edge to
            edge — the two read as one control that has come apart. `h-full` and `flex-col` pass the
            height through, or `<DataTablePanel fill>` has nothing to distribute and the pager floats
            under a short table.

            The table is `<PurchaseOrderLinesPanel>` — its own file, because a hook cannot follow
            this page's `if (!order) return null` guard. It carries the reversal from
            `<PreviewGrid>` to `<DataTablePanel>` and the reasoning for every column; read it
            there rather than here.
          */
          <div className="flex h-full min-w-0 flex-col p-4">
            <PurchaseOrderLinesPanel lines={live} />
          </div>
        )}
      </ProfileHeaderShell>

      {editing ? (
        <PurchaseOrderModal
          open
          editing={order}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelPurchaseOrderModal
          open
          order={{
            id: order.id,
            orderNumber: order.orderNumber,
            sourceRequestNumbers: order.sourceRequests.map((r) => r.requestNumber).join(', '),
          }}
          isSubmitted={order.status === PO_STATUS.submitted}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            setCancelling(false);
            load();
          }}
        />
      ) : null}

      {/*
        THE CONSEQUENCE IS STATED BEFORE THE TICK, in the record's own terms — what it opens, and
        that there is no undo. A dialog whose body only restates its own title is the ceremony the
        confirm rule is not asking for.

        NO `requireCredential`. Posting a document out is an ordinary act of somebody's job, and
        password ceremony on something done several times a day is ceremony people learn to click
        through. The destructive rule still governs delete and cancel.
      */}
      <ConfirmDialog
        id="purchase-order-mark-sent-confirm"
        open={markingSent}
        onClose={() => setMarkingSent(false)}
        onConfirm={() => void markSent()}
        title={`Mark ${order.orderNumber} as sent`}
        body={
          <>
            <p>
              This records that the order has gone out to {order.supplierName}. It is what opens
              the order to goods receipt — until it is marked sent, no delivery can be received
              against it.
            </p>
            <p>
              There is no way back: an order sent in error is cancelled, not un-sent. Its lines
              and quantities do not change.
            </p>
            {/*
              WHAT IS ABOUT TO BE RECORDED, before the tick — product call, 02 Oct 2026. The
              name and the instant are stamped from the SERVER, so the dialog says whose name
              goes on the record rather than offering a field for it: a date a reader types is
              a date a reader can misstate onto a permanent fact.
            */}
            <p>
              Your name and the current date and time are recorded against it.
            </p>
          </>
        }
        requireConfirm
        loading={acting}
      />

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="purchase-orders"
        /*
          SCOPED BY `entityId` ALONE, with no type bound. A record's own history matches on the
          parent id, and adding the type bound on top only subtracts — a write the server REFUSED
          never reached an entity and carries no entityType, and that is exactly the event a
          reader opens an order's history to find.
        */
        entityId={order.id}
      />
    </div>
  );
}

/**
 * What the banner SAYS — a glyph, a title, a sentence and a tone, one set per state.
 *
 * **Every state renders**, which is the banner rule this does not bend: a strip that appears only
 * when something is wrong is one the reader learns to skip, and then it is missed on the day it
 * matters.
 */
/*
  THE DISPATCH STAMP, as a clause the three dispatched banners append — product call,
  02 Oct 2026. It is ONE derivation read by all three rather than the sentence written three
  times, because a stamp worded differently on Sent and on Partially received would be two
  answers to one question on two states of one record.

  `== null`, never `===`: the API omits a null property entirely, so an order sent before the
  columns landed arrives as `undefined` and the strict test would be TRUE for it — appending a
  clause reading "sent by undefined".

  EITHER HALF ALONE STILL READS. The three are written together by the one path that sets them,
  so a half-stamped row is a state nothing can produce — but an order sent before 02 Oct 2026
  has neither, and the clause is simply absent there rather than saying so.
*/
function dispatchClause(order: PurchaseOrderDetail): string {
  if (order.sentAt == null && order.sentByName == null) return '';

  const when = order.sentAt == null ? null : formatDateTime(order.sentAt);
  const who = order.sentByName;

  if (when !== null && who != null) return ` Recorded as sent by ${who} on ${when}.`;
  if (when !== null) return ` Recorded as sent on ${when}.`;

  return ` Recorded as sent by ${who}.`;
}

function bannerFor(order: PurchaseOrderDetail): {
  icon: typeof Send;
  title: string;
  description: string;
} {
  if (!order.isActive) {
    return {
      icon: Trash2,
      title: 'Deleted',
      description:
        'This order is hidden from the register, its approval was closed, and the quantities it committed went back to the source requests. Restoring it takes them again.',
      
    };
  }

  switch (order.status) {
    case PO_STATUS.draft:
      return order.items.filter((l) => l.isActive).length === 0
        ? {
            icon: FileText,
            title: 'Draft — nothing on it yet',
            description: 'Add at least one line before it can be submitted for approval.',
            
          }
        : {
            icon: FileText,
            title: 'Draft',
            description:
              'It is still yours to change until it is submitted. Its quantities are already committed against the source requests, so nothing else can order the same lines.',
            
          };

    case PO_STATUS.submitted:
      return {
        icon: Send,
        title: 'Awaiting approval',
        description:
          'This order is in the approval cycle, waiting on the approvers. Track its progress on the Life cycle tab.',
        
      };

    /*
      AMBER, not emerald, and that is the directive rather than a tone preference. An approved
      order is not finished — it is WAITING ON THE READER to record that the document went out,
      and nothing can be received against it until they do. Emerald would say the opposite.
    */
    case PO_STATUS.approved:
      return {
        icon: CheckCircle2,
        title: 'Approved — not sent yet',
        description:
          'The order is committed. Record that it has gone out to the supplier: until it is marked sent, no delivery can be received against it.',
        
      };

    case PO_STATUS.sent:
      return {
        icon: Truck,
        title: 'Sent to supplier',
        description:
          'The document is out and the order is awaiting delivery. A goods receipt can now be registered against it.'
          + dispatchClause(order),
        
      };

    case PO_STATUS.partiallyReceived:
      return {
        icon: PackageCheck,
        title: 'Partially received',
        description:
          'Some of what was ordered has arrived and been accepted; the rest is still open with the supplier. Register another goods receipt as the balance lands.'
          + dispatchClause(order),
        
      };

    case PO_STATUS.fullyReceived:
      return {
        icon: PackageCheck,
        title: 'Fully received',
        description:
          'Every line has been received in full and the order is finished. Correcting stock that has landed is a stock adjustment, which touches no order.',
        
      };

    case PO_STATUS.rejected:
      return {
        icon: XCircle,
        title: 'Rejected',
        description:
          'The reason is on the Life cycle tab. It is a draft again — correct it and submit it once more, or cancel it to give its quantities back.',
        
      };

    /*
      CANCELLED IS ITS OWN CASE NOW, and the default no longer impersonates it. Three members —
      5, 6 and 7 — reach this switch, and while the default said "Cancelled" a received order
      was described to the reader as withdrawn, with its quantities claimed to have gone back.
      A default arm that names one state is a default arm that lies about every other.
    */
    case PO_STATUS.cancelled:
      return {
        icon: CircleSlash,
        title: 'Cancelled',
        description:
          order.cancellationReason
          ?? 'The register keeps it, marked cancelled. Its quantities went back to the source requests.',
        
      };

    default:
      return {
        icon: CircleSlash,
        title: order.statusLabel,
        description: 'This order is not in a state this page has anything further to say about.',
        
      };
  }
}
